import { ASSET_SPECS } from 'fym-shared/src/music24/assetSpecs';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import {
  CatalogItem,
  GetReleaseKitResponse,
  ReleaseKitSummary,
  SerializedKitAsset,
} from '../../../../../redux/api/fymAPI.types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { PromoteMediaDraft } from '../../draftStore';
import { inlineMediaUrl } from '../../inlineMediaUrl';
import { PostMediaKind } from '../../platformMeta';
import { SerializedReel } from '../../types';
import { buildRevisionCards, RevisionCard } from '../../releases/assets/revisionCards';
import { fileHasWatermark } from '../../../watermarkUpsell/files/watermarkedFile';

/**
 * The media picker of the Composer: one file per card of the Assets tab (the newest ready revision
 * of each asset type + variant, and of each AI reel length). Pure data, so it is tested without React.
 */
export interface PickerFile {
  /** The key of the card (`kitAsset:<themeId>:<variant>` or `reel:<variant>`). */
  key: string;
  card: RevisionCard;
  themeId: StudioThemeId;
  mediaKind: PostMediaKind;
  /** The FileModel id that the post sends as `mediaFileId`. */
  fileId: number;
  /** The relative `files/<uuid>/download` url of the file. */
  fileUrl: string;
  /** The relative url of the picture: the thumbnail, else the poster of a video, the file of an image. */
  pictureUrl: string | null;
  /** Pixels; null when not known. */
  width: number | null;
  height: number | null;
  /** Null for an image. */
  durationSec: number | null;
  /** The revision number of the file (r1, r2). */
  revision: number;
  madeAt: string;
  /** The file carries the Music24 watermark (design: trial-upsell a5). */
  watermark: boolean;
}

export type PickerFilter = 'all' | PostMediaKind;

/**
 * The motion artwork is for the distributor (Apple Music, Spotify), not a social post. The Library
 * "Post it" rule left it out too (`libraryPostAspect`).
 */
const NOT_POSTABLE: ReadonlyArray<StudioThemeId> = ['motionArtwork'];

/** The reel of a card that has a video: the newest, else the older ready one. */
function readyReelOf(card: RevisionCard): SerializedReel | null {
  const newest = card.reel;
  if (newest !== null && (newest.status === 'ready' || newest.status === 'approved') && newest.video !== null) {
    return newest;
  }
  return card.readyReel;
}

/**
 * The kit asset of a card that has a file: the newest revision when it is ready, else the newest
 * ready revision of the same type + variant (`ready` of the kit answer). The card of the Assets tab
 * has no ready revision while a newer one renders (its `readyAsset` is null then), so the picker
 * does not read it: the ready file stays in the picker while the next revision is made.
 */
function readyAssetOf(card: RevisionCard, readyByKey: Map<string, SerializedKitAsset>): SerializedKitAsset | null {
  if (card.asset?.status === 'ready') {
    return card.asset;
  }
  return readyByKey.get(card.key) ?? card.readyAsset;
}

function revisionOf(card: RevisionCard, isNewest: boolean): number {
  return isNewest ? card.revision : (card.readyRevision ?? card.revision);
}

/**
 * The picture of a tile: the small thumbnail of the asset (the 640 × 800 JPEG of the work emails,
 * `thumbUrl`), else its poster, else the file of an image. A tile never loads a full kit file
 * while a thumbnail exists.
 */
function assetPictureUrl(asset: SerializedKitAsset): string | null {
  return asset.thumbUrl ?? asset.posterUrl ?? (asset.durationSec === null ? asset.fileUrl : null);
}

function kitAssetFile(card: RevisionCard, readyByKey: Map<string, SerializedKitAsset>): PickerFile | null {
  const asset = readyAssetOf(card, readyByKey);
  if (asset === null || asset.fileRemoved === true || asset.fileId === null || asset.fileUrl === null) {
    return null;
  }
  const spec = ASSET_SPECS[asset.specId] as (typeof ASSET_SPECS)[keyof typeof ASSET_SPECS] | undefined;
  const mediaKind: PostMediaKind = spec?.kind ?? (asset.durationSec === null ? 'image' : 'video');
  return {
    key: card.key,
    card,
    themeId: card.themeId,
    mediaKind,
    fileId: asset.fileId,
    fileUrl: asset.fileUrl,
    pictureUrl: assetPictureUrl(asset),
    width: asset.width ?? spec?.width ?? null,
    height: asset.height ?? spec?.height ?? null,
    durationSec: mediaKind === 'video' ? asset.durationSec : null,
    revision: asset.version,
    madeAt: asset.renderedAt ?? asset.createdAt,
    watermark: fileHasWatermark(asset),
  };
}

function reelFile(card: RevisionCard): PickerFile | null {
  const reel = readyReelOf(card);
  if (reel === null || reel.video === null) {
    return null;
  }
  return {
    key: card.key,
    card,
    themeId: 'aiReel',
    mediaKind: 'video',
    fileId: reel.video.fileId,
    fileUrl: reel.video.url,
    pictureUrl: reel.video.posterUrl,
    // An AI reel is always 9:16; the pixel size depends on the reel (a preview render is half size).
    width: null,
    height: null,
    durationSec: reel.durationSec,
    revision: revisionOf(card, reel === card.reel),
    madeAt: reel.createdAt,
    watermark: fileHasWatermark(reel),
  };
}

/**
 * The parts of a release kit answer that the picker reads. A summary of `GET release-kits?include=assets`
 * gives the same parts (its `assets` are the ready current versions), so the SmartLink Media block
 * lists the files of every song with one request.
 */
export type PickerKitAnswer = Pick<GetReleaseKitResponse, 'assets' | 'newest' | 'ready' | 'reels'>;

/** Every file of a song that the Composer can attach, newest first. */
export function pickerFiles(answer: PickerKitAnswer | undefined): PickerFile[] {
  if (answer === undefined) {
    return [];
  }
  const cards = buildRevisionCards({ assets: answer.assets, newest: answer.newest, reels: answer.reels });
  const readyByKey = new Map(
    (answer.ready ?? []).map((asset) => [`kitAsset:${asset.themeId}:${asset.variant}`, asset] as const)
  );
  return cards
    .filter((card) => !NOT_POSTABLE.includes(card.themeId))
    .map((card) => (card.kind === 'reel' ? reelFile(card) : kitAssetFile(card, readyByKey)))
    .filter((file): file is PickerFile => file !== null);
}

/** The files of one filter chip. */
export function filterPickerFiles(files: PickerFile[], filter: PickerFilter): PickerFile[] {
  return filter === 'all' ? files : files.filter((file) => file.mediaKind === filter);
}

/**
 * True when the song has a file that the Composer can post: a ready kit file that is not the
 * motion artwork (`NOT_POSTABLE`), or a ready AI reel. Reads a summary of `GET release-kits?include=assets`;
 * a summary with no `assets` (past the newest 50 songs) counts as none.
 */
export function hasPostableFile(kit: ReleaseKitSummary): boolean {
  const assets = kit.assets ?? [];
  const reels = kit.reels ?? [];
  return (
    assets.some(
      (asset) =>
        asset.status === 'ready' &&
        !NOT_POSTABLE.includes(asset.themeId) &&
        asset.fileId !== null &&
        asset.fileRemoved !== true
    ) || reels.some((reel) => reel.video !== null)
  );
}

/**
 * The song the picker opens on: the release that the Composer already picked, else the song whose
 * kit changed last and has a file that the Composer can post (a song with only a motion artwork
 * would open on an empty list). Undefined when no song has one.
 */
export function defaultPickerSong(
  composerReleaseUuid: string | undefined,
  kits: ReleaseKitSummary[] | undefined
): string | undefined {
  if (composerReleaseUuid !== undefined) {
    return composerReleaseUuid;
  }
  const postable = (kits ?? []).filter(hasPostableFile);
  const newest = [...postable].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  return newest?.catalogItemUuid;
}

/** The ratios of the kit shapes, for the aspect label. */
const KNOWN_RATIOS: ReadonlyArray<{ label: string; ratio: number }> = [
  { label: '9:16', ratio: 9 / 16 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '1:1', ratio: 1 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '1.91:1', ratio: 1.91 },
  { label: '3:1', ratio: 3 },
];

/** "9:16", "4:5", "1:1"; the pixel size for a shape with no common name; "9:16" for a reel. */
export function pickerAspectLabel(file: {
  themeId?: StudioThemeId;
  width: number | null;
  height: number | null;
}): string {
  if (file.width === null || file.height === null || file.width <= 0 || file.height <= 0) {
    return file.themeId === 'aiReel' ? '9:16' : '';
  }
  const ratio = file.width / file.height;
  const known = KNOWN_RATIOS.find((one) => Math.abs(one.ratio - ratio) / one.ratio < 0.02);
  return known?.label ?? `${file.width} × ${file.height}`;
}

/** The Composer media of a picked file. `label` is the card name ("Story (Out now)"). */
export function mediaDraftOfPickerFile(
  file: PickerFile,
  item: CatalogItem | undefined,
  label: string
): PromoteMediaDraft {
  return {
    fileId: file.fileId,
    fileUrl: getFullAPIFileURL(file.fileUrl),
    thumbnailUrl: file.pictureUrl === null ? undefined : inlineMediaUrl(file.pictureUrl),
    kind: file.mediaKind,
    aspect: pickerAspectLabel(file),
    width: file.width,
    height: file.height,
    durationSec: file.durationSec,
    label,
    title: item?.title ?? '',
    artistName: item?.artistName ?? '',
    catalogItemUuid: item?.uuid,
    smartLinkId: item?.smartLinkId ?? undefined,
    releaseDate: item?.releaseDate ?? undefined,
    renderedAt: file.madeAt,
    themeId: file.themeId,
    watermark: file.watermark,
  };
}
